import type { AppState, AnalysisState } from './types'
import { store } from './core'


// Original convenience functions preserved
export function resetActiveViewData(): void {
  store.getState().resetActiveViewData()
}

export function setActiveViewData(viewId: string, data: any, filters?: Record<string, any>): void {
  store.getState().setActiveViewData(viewId, data, filters)
}

export function clearAllViewData(): void {
  store.getState().clearAllViewData()
}

export function clearViewData(viewId: string): void {
  store.getState().clearViewData(viewId)
}

export function trySetFromExistingViewData(viewId: string): void {
  store.getState().trySetFromExistingViewData(viewId)
}

export function setStoreKV<K extends keyof AppState>(key: K, value: AppState[K]): void {
  store.getState().setStoreKV(key, value)
}

export function setStorePartial(obj: Partial<AppState>): void {
  store.getState().setStorePartial(obj)
}

export function getStore<T extends keyof AppState>(key: T): AppState[T] {
  return store.getState()[key]
}

export function getStoreState(): AppState {
  return store.getState()
}

// Analysis state helpers
export function resetAnalysisState(): void {
  store.getState().resetAnalysisState()
}

export function getAnalysisState(): AnalysisState {
  return store.getState().getAnalysisState()
}

export function setAnalysisState<K extends keyof AnalysisState>(key: K, value: AnalysisState[K]): void {
  store.getState().setAnalysisState(key, value)
}

export function updateAnalysisFlag<K extends keyof AnalysisState['initFlags']>(flag: K, value: boolean): void {
  store.getState().updateAnalysisFlag(flag, value)
}

export function updateAnalysisComponent<K extends keyof AnalysisState['uiComponents']>(component: K, value: AnalysisState['uiComponents'][K]): void {
  store.getState().updateAnalysisComponent(component, value)
}

export function updateAnalysisConfig<K extends keyof AnalysisState['config']>(config: K, value: AnalysisState['config'][K]): void {
  store.getState().updateAnalysisConfig(config, value)
}

export const getAllSalesData = () => getAnalysisState().allSalesData
export const setAllSalesData = (data: AnalysisState['allSalesData']) => setAnalysisState('allSalesData', data)

export const getCharts = () => getAnalysisState().charts
export const setCharts = (charts: AnalysisState['charts']) => setAnalysisState('charts', charts)

export const setChartProperty = (key: string, value: any) => {
  const currentCharts = getCharts()
  const updatedCharts = { ...currentCharts, [key]: value }
  setCharts(updatedCharts)
}

export const getChartProperty = (key: string) => {
  return getCharts()[key]
}

export const getChartDataForAI = () => getAnalysisState().chartDataForAI
export const setChartDataForAI = (data: AnalysisState['chartDataForAI']) => setAnalysisState('chartDataForAI', data)

export const setChartDataForAIProperty = (key: string, value: any) => {
  const currentData = getChartDataForAI()
  const updatedData = { ...currentData, [key]: value }
  setChartDataForAI(updatedData)
}

export const getAiAnalysisResults = () => getAnalysisState().aiAnalysisResults
export const setAiAnalysisResults = (results: AnalysisState['aiAnalysisResults']) => setAnalysisState('aiAnalysisResults', results)

export const getCurrentPnlData = () => getAnalysisState().currentPnlData
export const setCurrentPnlData = (data: AnalysisState['currentPnlData']) => setAnalysisState('currentPnlData', data)

export const getInitFlag = <T extends keyof AnalysisState['initFlags']>(flag: T) => {
  return getAnalysisState().initFlags[flag] || false
}

export const setInitFlag: typeof updateAnalysisFlag = (...params) => {
  updateAnalysisFlag(...params)
}

export const getUIComponent = <T extends keyof AnalysisState['uiComponents']>(component: T) => {
  return getAnalysisState().uiComponents[component] || null
}

export const setUIComponent = <
  K extends keyof AnalysisState['uiComponents'],
  V extends AnalysisState['uiComponents'][K],
>(component: K, value: V, touchFn?: ($value: V) => void): V => {
  updateAnalysisComponent(component, value)
  touchFn?.(value)
  return value
}

export const getConfigValue = <T extends keyof AnalysisState['config']>(config: T) => {
  return getAnalysisState().config[config]
}

export const setConfigValue: typeof updateAnalysisConfig = (...params) => {
  updateAnalysisConfig(...params)
}

// Query scope helpers
export const getQueryScope = (): 'current' | 'all' => {
  return getConfigValue('queryScope')
}

export const setQueryScope = (scope: 'current' | 'all') => {
  setConfigValue('queryScope', scope)
}

export const getAccountContext = () => {
  const { accountId, planType, role, userBrands, selectedBrandId, userBranches, selectedBranchId, tierLimits, members } = getAnalysisState()
  const normalizedBranches = userBranches.map((branch) => ({
    ...branch,
    isDefaultName: branch.isDefaultName === false ? false : true,
  }))
  // Return the new Brand properties AND the existing Branch properties
  return { accountId, planType, role, userBrands, selectedBrandId, userBranches: normalizedBranches, selectedBranchId, tierLimits, members }
}

export const setAccountContext = (context: {
  accountId: string | null
  planType: 'free' | 'standard' | 'enterprise'
  role: 'owner' | 'member' | null
  userBrands: { brandId: string, brandName: string }[]
  selectedBrandId: string | null
  // --- THIS IS THE FIX ---
  // We must add brandId here to match the data from auth.ts
  userBranches: { 
    branchId: string, 
    branchName: string, 
    role: 'owner' | 'member', 
    isDefaultName: boolean, 
    accountId: string,
    brandId: string | null // <-- ADDED THIS LINE
  }[]
  // --- END FIX ---
  selectedBranchId: string | null
  tierLimits?: {
    branchLimit: number | null
    memberLimit: number
    features: string[]
  } | null
  members?: {
    userId: string
    email: string
    role: 'owner' | 'member'
    joinedAt: Date | null
  }[]
}) => {
  store.getState().setAccountContext(context)
}

export const getSelectedBranchId = () => getAnalysisState().selectedBranchId
export const setSelectedBranchId = (branchId: string | null) => {
  store.getState().setSelectedBranchId(branchId)
}

/**
 * Subscribe to changes of the selectedBranchId in the analysis state.
 * The listener is called with the new branchId (string|null) whenever it changes.
 * Returns an unsubscribe function.
 */
export const subscribeToSelectedBranch = (listener: (branchId: string | null) => void) => {
  let prev = getSelectedBranchId()
  const unsub = store.subscribe((state) => {
    const current = state.analysisState.selectedBranchId
    if (current !== prev) {
      prev = current
      try { listener(current) } catch (err) { console.error('subscribeToSelectedBranch listener error', err) }
    }
  })
  return unsub
}

export const getUserBrands = () => getAnalysisState().userBrands
export const getSelectedBrandId = () => getAnalysisState().selectedBrandId
export const setSelectedBrandId = (brandId: string | null) => {
  // 1. Update the in-memory state
  store.getState().setSelectedBrandId(brandId)
  
  // --- THIS IS THE FIX ---
  // 2. Save the selection to localStorage so it persists on reload
  try {
    if (brandId) {
      localStorage.setItem('selectedBrandId', brandId)
    } else {
      localStorage.removeItem('selectedBrandId')
    }
  } catch (e) {
    console.warn('Could not save selectedBrandId to localStorage', e)
  }
  // --- END FIX ---
}
export const subscribeToSelectedBrand = (listener: (brandId: string | null) => void) => {
  let prev = getSelectedBrandId()
  const unsub = store.subscribe((state) => {
    const current = state.analysisState.selectedBrandId
    if (current !== prev) {
      prev = current
      try { listener(current) } catch (err) { console.error('subscribeToSelectedBrand listener error', err) }
    }
  })
  return unsub
}

// Plan and tier helpers
export const getPlanType = () => getAnalysisState().planType
export const getUserBranches = () => getAnalysisState().userBranches.map((branch) => ({
  ...branch,
  isDefaultName: branch.isDefaultName === false ? false : true,
}))
export const getRole = () => getAnalysisState().role
export const getTierLimits = () => getAnalysisState().tierLimits
export const getMembers = () => getAnalysisState().members

/**
 * Get plan limits for current tier (local cache)
 */
export const getPlanLimits = () => {
  const tierLimits = getTierLimits()
  if (tierLimits) {
    return tierLimits
  }

  // Fallback to default limits based on plan type
  const planType = getPlanType()
  const defaultLimits = {
    free: {
      branchLimit: 1,
      memberLimit: 1,
      features: ['dashboard', 'upload', 'basicAnalysis'],
    },
    standard: {
      branchLimit: 1,
      memberLimit: 100,
      features: ['dashboard', 'upload', 'basicAnalysis', 'advancedAnalysis', 'aiInsights', 'memberInvites'],
    },
    enterprise: {
      branchLimit: null, // unlimited
      memberLimit: 100,
      features: ['dashboard', 'upload', 'basicAnalysis', 'advancedAnalysis', 'aiInsights', 'memberInvites', 'branchComparison', 'pdfExport', 'apiAccess'],
    },
  }

  return (defaultLimits as any)[planType] || defaultLimits.free
}

/**
 * Fetch plan limits from backend and update Zustand store
 * Should be called on app initialization or after plan changes
 */
export const fetchAndCachePlanLimits = async () => {
  try {
    const { functions } = await import('@/core/firebase')
    const { httpsCallable } = await import('firebase/functions')

    const getPlanLimitsCallable = httpsCallable(functions, 'getPlanLimits')
    const result = await getPlanLimitsCallable()
    const data = result.data as any

    // T033-T034: Check for expired subscription
    let effectivePlanType: 'free' | 'standard' | 'enterprise' = (data.planType as any) || 'free'
    let isExpired = false

    if (data.subscription?.possibleExpiredAt) {
      const expirationDate = new Date(data.subscription.possibleExpiredAt)
      const now = new Date()

      if (expirationDate < now) {
        // T034: Subscription expired - downgrade to free tier
        effectivePlanType = 'free'
        isExpired = true
        console.warn('Subscription expired on', expirationDate.toISOString())

        // T035: Clear IndexedDB cache on expiration
        try {
          const { clearSummariesCache } = await import('@/services/localCacheService')
          await clearSummariesCache()
          console.log('Cache cleared due to subscription expiration')
        } catch (cacheError) {
          console.error('Failed to clear cache on expiration:', cacheError)
        }
      }
    }

    // Update store with fetched limits
    const tierLimits = {
      branchLimit: data.limits?.branchLimit ?? data.limits?.branchLimitCount ?? null,
      memberLimit: data.limits?.memberLimit ?? 100,
      features: Object.entries(data.features || {})
        .filter(([_, enabled]) => enabled)
        .map(([feature]) => feature),
    }

    // Choose the higher plan between current store and fetched plan (avoid accidental downgrade)
    const currentPlan = getPlanType()
    const rank = { free: 0, standard: 1, enterprise: 2 } as const
    const finalPlanType = rank[effectivePlanType] >= rank[currentPlan] ? effectivePlanType : currentPlan

    const existingContext = getAccountContext()
    const ownedAccountIds = existingContext.userBranches
      .filter((branch) => branch.role === 'owner')
      .map((branch) => branch.accountId)
    const ownsFetchedAccount = data.account?.id ? ownedAccountIds.includes(data.account.id) : false
    const enforcedPlanType = ownsFetchedAccount ? finalPlanType : 'free'
    const enforcedTierLimits = ownsFetchedAccount
      ? tierLimits
      : {
        branchLimit: 1,
        memberLimit: 1,
        features: [],
      }

    // Update account context with fresh data (do not downgrade plan)
    setAccountContext({
      accountId: ownsFetchedAccount ? (data.account?.id || null) : existingContext.accountId,
      planType: enforcedPlanType as any,
      role: existingContext.role,
      userBranches: existingContext.userBranches,
      selectedBranchId: existingContext.selectedBranchId,
      tierLimits: enforcedTierLimits,
      members: existingContext.members,
    })

    // Return data with expiration flag
    return { ...data, isExpired, effectivePlanType: enforcedPlanType }
  } catch (error) {
    console.error('Failed to fetch plan limits:', error)
    // T039: Default to 'free' tier on error (fail-safe)
    setAccountContext({
      accountId: null,
      planType: 'free',
      role: null,
      userBranches: [],
      selectedBranchId: null,
      tierLimits: null,
      members: [],
    })
    // Return local cached limits on error
    return getPlanLimits()
  }
}

/**
 * Check if the current subscription is expired
 * @returns true if subscription is expired, false otherwise
 */
export const isSubscriptionExpired = (): boolean => {
  const analysisState = getAnalysisState()
  // Check if user was on a paid tier but now shows as free (likely expired)
  // This is a heuristic - actual expiration check happens in fetchAndCachePlanLimits
  return analysisState.planType === 'free' && (analysisState.tierLimits?.features.length ?? 0) === 0
}

/**
 * Check if user has a specific feature based on tier
 */
export const hasFeature = (featureName: string): boolean => {
  const limits = getPlanLimits()
  return limits.features.includes(featureName)
}

/**
 * Check if user is on a paid tier (standard or premium)
 */
export const isPaidTier = (): boolean => {
  const planType = getPlanType()
  return planType === 'standard' || planType === 'enterprise'
}

/**
 * Check if user is the owner of the current branch
 */
export const isBranchOwner = (): boolean => {
  const role = getRole()
  return role === 'owner'
}

/**
 * Get the required tier for a feature
 */
export const getRequiredTierForFeature = (featureName: string): 'free' | 'standard' | 'enterprise' | null => {
  const tiers: ('free' | 'standard' | 'enterprise')[] = ['free', 'standard', 'enterprise']
  const featureMap = {
    free: ['dashboard', 'upload', 'basicAnalysis'],
    standard: ['advancedAnalysis', 'aiInsights', 'memberInvites'],
    enterprise: ['branchComparison', 'pdfExport', 'apiAccess'],
  }

  for (const tier of tiers) {
    if (featureMap[tier].includes(featureName)) {
      return tier
    }
  }

  return null
}

/**
 * Check if user can create more branches
 */
export const canCreateBranch = (): boolean => {
  const limits = getPlanLimits()
  const branches = getUserBranches()

  if (limits.branchLimit === null) {
    return true // unlimited
  }

  return branches.length < limits.branchLimit
}

/**
 * Check if branch can accept more members
 */
export const canInviteMember = (): boolean => {
  const limits = getPlanLimits()
  const members = getMembers()

  if (!isPaidTier()) {
    return false // Free tier cannot invite
  }

  return members.length < limits.memberLimit
}

/**
 * Check if current user is an admin (app-level)
 * Reads from Firestore instead of custom claims
 *
 * @param uid - Firebase Auth UID
 * @returns true if user is admin, false otherwise
 */
export const isUserAdmin = async (uid: string): Promise<boolean> => {
  try {
    const { db } = await import('@/core/firebase')
    const { doc, getDoc } = await import('firebase/firestore')

    const adminDocRef = doc(db, 'adminUsers', uid)
    const adminDocSnap = await getDoc(adminDocRef)

    if (!adminDocSnap.exists()) {
      return false
    }

    const data = adminDocSnap.data()
    return data.role === 'admin'
  } catch (error) {
    console.error('Error checking admin status:', error)
    return false
  }
}

/**
 * Fetch user's active subscription from Firestore
 *
 * @param accountId - Account ID
 * @param branchId - Optional branch ID to filter subscriptions
 * @returns Subscription data or null
 */
export const fetchUserSubscription = async (
  accountId: string,
  branchId?: string,
): Promise<any | null> => {
  try {
    const { db } = await import('@/core/firebase')
    const { collection, query, where, orderBy, limit, getDocs } = await import('firebase/firestore')

    const subscriptionsRef = collection(db, 'accounts', accountId, 'subscriptions')

    // Query for subscriptions ordered by activatedAt (most recent first)
    let q = query(subscriptionsRef, orderBy('activatedAt', 'desc'), limit(1))

    // If branchId provided, filter by subscriptions that include this branch
    if (branchId) {
      q = query(subscriptionsRef, where('branchIds', 'array-contains', branchId), orderBy('activatedAt', 'desc'), limit(1))
    }

    const snapshot = await getDocs(q)

    if (snapshot.empty) {
      return null
    }

    const doc = snapshot.docs[0]!
    const data = doc.data()

    // Check if subscription is expired
    if (data.possibleExpiredAt) {
      const expirationDate = data.possibleExpiredAt.toDate ? data.possibleExpiredAt.toDate() : new Date(data.possibleExpiredAt)
      const now = new Date()

      if (expirationDate < now) {
        return null // Subscription expired
      }
    }

    return { id: doc.id, ...data }
  } catch (error) {
    console.error('Error fetching user subscription:', error)
    return null
  }
}

export { store } from './core'
export * from './types'
